import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../lib/api';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'user';
  must_change_password: boolean;
  is_super_admin?: boolean;
}

interface Tenant {
  name: string;
  plan: string;
  trial_ends_at?: string;
  branding_logo_url?: string;
  branding_primary_color?: string;
  branding_company_name?: string;
}

interface AuthContextValue {
  user: User | null;
  tenant: Tenant | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  sessionExpiresAt: number | null;
  login: (email: string, password: string) => Promise<{ requiresMfa: boolean; challengeId?: string; requiresEnrollment?: boolean }>;
  verifyMfa: (challengeId: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  extendSession: () => Promise<void>;
}

// Decode JWT to get expiry time (without verifying signature)
function getTokenExpiry(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    if (decoded.exp) {
      return decoded.exp * 1000; // Convert to milliseconds
    }
    return null;
  } catch {
    return null;
  }
}

const AuthContext = createContext<AuthContextValue | null>(null);

const ACCESS_TOKEN_KEY = 'securesend_access_token';
const REFRESH_TOKEN_KEY = 'securesend_refresh_token';

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionExpiresAt, setSessionExpiresAt] = useState<number | null>(null);

  // Centralized auth failure handler
  const handleAuthFailure = () => {
    // Clear all auth state
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    api.setAccessToken(null);
    setUser(null);
    setTenant(null);
    setSessionExpiresAt(null);

    // Redirect to login with expired flag
    navigate('/login?expired=true', { replace: true });
  };

  // Initialize auth state from stored tokens
  useEffect(() => {
    const initAuth = async () => {
      const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

      if (!accessToken || !refreshToken) {
        setIsLoading(false);
        return;
      }

      api.setAccessToken(accessToken);
      setSessionExpiresAt(getTokenExpiry(accessToken));

      try {
        const { user, tenant } = await api.getMe();
        setUser(user);
        setTenant(tenant);
      } catch (error) {
        // Try to refresh the token
        try {
          const { session } = await api.refreshToken(refreshToken);
          localStorage.setItem(ACCESS_TOKEN_KEY, session.access_token);
          localStorage.setItem(REFRESH_TOKEN_KEY, session.refresh_token);
          api.setAccessToken(session.access_token);
          setSessionExpiresAt(getTokenExpiry(session.access_token));

          const { user, tenant } = await api.getMe();
          setUser(user);
          setTenant(tenant);
        } catch {
          // Clear invalid tokens
          localStorage.removeItem(ACCESS_TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
          api.setAccessToken(null);
          setSessionExpiresAt(null);
        }
      }

      setIsLoading(false);
    };

    initAuth();
  }, []);

  // Register auth failure callback with API client
  useEffect(() => {
    api.setOnAuthFailure(handleAuthFailure);
  }, []);

  const login = async (email: string, password: string) => {
    const response = await api.login(email, password);

    // Store session for MFA enrollment flow
    if (response.session) {
      localStorage.setItem(ACCESS_TOKEN_KEY, response.session.access_token);
      localStorage.setItem(REFRESH_TOKEN_KEY, response.session.refresh_token);
      api.setAccessToken(response.session.access_token);
      setSessionExpiresAt(getTokenExpiry(response.session.access_token));
    }

    // Check if MFA enrollment is required (not enrolled yet)
    if (!response.mfa_enrolled) {
      return { requiresMfa: false, requiresEnrollment: true };
    }

    // Check if MFA verification is required (enrolled but not verified)
    if (response.requires_mfa) {
      return { requiresMfa: true, challengeId: response.mfa_challenge_id };
    }

    // MFA not required (already at aal2), fetch user
    const { user, tenant } = await api.getMe();
    setUser(user);
    setTenant(tenant);

    return { requiresMfa: false };
  };

  const verifyMfa = async (challengeId: string, code: string) => {
    // Get current refresh token to send to backend for session refresh
    const currentRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!currentRefreshToken) {
      throw new Error('Session expired. Please log in again.');
    }

    const { session } = await api.verifyMfa(challengeId, code, currentRefreshToken);

    if (!session) {
      throw new Error('Failed to get new session after MFA verification');
    }

    localStorage.setItem(ACCESS_TOKEN_KEY, session.access_token);
    localStorage.setItem(REFRESH_TOKEN_KEY, session.refresh_token);
    api.setAccessToken(session.access_token);
    setSessionExpiresAt(getTokenExpiry(session.access_token));

    const { user, tenant } = await api.getMe();
    setUser(user);
    setTenant(tenant);
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch (err) {
      // Server logout failed, but we still clear local session
      console.warn('Server logout failed, clearing local session:', err);
    }

    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    api.setAccessToken(null);
    setUser(null);
    setTenant(null);
    setSessionExpiresAt(null);
  };

  const refreshUser = async () => {
    const { user, tenant } = await api.getMe();
    setUser(user);
    setTenant(tenant);
  };

  const extendSession = async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const { session } = await api.refreshToken(refreshToken);
    localStorage.setItem(ACCESS_TOKEN_KEY, session.access_token);
    localStorage.setItem(REFRESH_TOKEN_KEY, session.refresh_token);
    api.setAccessToken(session.access_token);
    setSessionExpiresAt(getTokenExpiry(session.access_token));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        tenant,
        isLoading,
        isAuthenticated: !!user,
        isSuperAdmin: !!user?.is_super_admin,
        sessionExpiresAt,
        login,
        verifyMfa,
        logout,
        refreshUser,
        extendSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
